














































import { Issue } from "@/http/api/v2/issue";
import { FeatureAnalytics } from "@/http/api/v2/featureAnalytics";
import { FeatureAnalyticsAction } from "@/http/dto/featureAnalytics";
import { IssueStatus, IssueStatusSummaryDto } from "@/http/dto/issue";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

@Component
export default class IssueStatusSummary extends Vue {
  @Prop({ required: true }) hotelObjectId!: string;
  @Prop({ default: () => [] }) selectedStatuses!: IssueStatus[];
  @Prop({ default: 0 }) refreshToken!: number;

  periodDays: number = 7;
  overdueHours: number = 24;
  loading: boolean = false;
  loadError: boolean = false;
  summary: IssueStatusSummaryDto | null = null;
  refreshTimer: number | null = null;
  viewTracked: boolean = false;

  readonly overdueHourOptions = [1, 3, 6, 12, 24, 48];
  readonly statusItems = [
    { label: "새이슈", status: IssueStatus.REGISTERED, className: "registered" },
    { label: "진행중", status: IssueStatus.NOTIFIED, className: "notified" },
    { label: "보류중", status: IssueStatus.PENDING, className: "pending" },
  ];

  mounted() {
    this.restoreSettings();
    this.loadSummary();
  }

  @Watch("hotelObjectId")
  onHotelChanged() {
    this.viewTracked = false;
    this.restoreSettings();
    this.loadSummary();
  }

  @Watch("refreshToken")
  onRefreshTokenChanged() {
    this.scheduleSummaryRefresh();
  }

  beforeDestroy() {
    if (this.refreshTimer != null) {
      window.clearTimeout(this.refreshTimer);
    }
  }

  get storageKey() {
    return `issue-status-summary:${this.hotelObjectId}`;
  }

  restoreSettings() {
    if (!this.hotelObjectId) {
      return;
    }

    try {
      const saved = JSON.parse(localStorage.getItem(this.storageKey) || "{}");
      const periodDays = Number(saved.periodDays);
      const overdueHours = Number(saved.overdueHours);

      this.periodDays = [7, 30, 90].includes(periodDays) ? periodDays : 7;
      this.overdueHours = this.overdueHourOptions.includes(overdueHours) ? overdueHours : 24;
    } catch {
      this.periodDays = 7;
      this.overdueHours = 24;
    }
  }

  saveSettings() {
    if (!this.hotelObjectId) {
      return;
    }

    localStorage.setItem(
      this.storageKey,
      JSON.stringify({
        periodDays: this.periodDays,
        overdueHours: this.overdueHours,
      }),
    );
  }

  async loadSummary() {
    if (!this.hotelObjectId) {
      return;
    }

    this.loading = true;
    this.loadError = false;

    try {
      this.summary = await Issue.getIssueStatusSummary(
        this.hotelObjectId,
        this.periodDays,
        this.overdueHours,
      );
      if (!this.viewTracked) {
        this.track("summary_view");
        this.viewTracked = true;
      }
    } catch {
      this.loadError = true;
    } finally {
      this.loading = false;
    }
  }

  scheduleSummaryRefresh() {
    if (this.refreshTimer != null) {
      window.clearTimeout(this.refreshTimer);
    }

    this.refreshTimer = window.setTimeout(() => {
      this.refreshTimer = null;
      this.loadSummary();
    }, 250);
  }

  changePeriod() {
    this.saveSettings();
    this.loadSummary();
    this.track("period_change");
    this.$emit("period-change", this.periodDays);
  }

  changeOverdueHours() {
    this.saveSettings();
    this.loadSummary();
    this.track("overdue_change");
    this.$emit("overdue-change", this.overdueHours);
  }

  selectStatus(status: IssueStatus) {
    this.track(this.isSelected(status) ? "status_filter_clear" : "status_filter_apply", status);
    this.$emit("status-select", {
      status,
      periodDays: this.periodDays,
      overdueHours: this.overdueHours,
    });
  }

  track(action: FeatureAnalyticsAction, status?: IssueStatus) {
    FeatureAnalytics.trackIssueStatusDashboard(this.hotelObjectId, {
      action,
      source: "issue_status_dashboard",
      status,
      periodDays: this.periodDays,
      overdueHours: this.overdueHours,
    });
  }

  isSelected(status: IssueStatus) {
    return this.selectedStatuses.length === 1 && this.selectedStatuses[0] === status;
  }

  statusCount(status: IssueStatus) {
    return this.summary?.statuses.find(item => item.status === status)?.count || 0;
  }

  overdueCount(status: IssueStatus) {
    return this.summary?.statuses.find(item => item.status === status)?.overdueCount || 0;
  }
}
